import React, { useCallback, useEffect, useState } from 'react'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import { HandHeart, Loader2, AlertCircle, Trash2, Eye, Building2 } from 'lucide-react'
import {
  getSupporterRegistrations,
  getSupporterStats,
  updateSupporterStatus,
  deleteSupporterRegistration,
  REGISTRATION_TYPE_LABELS,
  SUPPORT_TYPE_LABELS,
  SUPPORTER_STATUS_LABELS,
  type SupporterRegistration,
  type RegistrationStatus,
  type RegistrationType,
} from '@/services/supporterService'

const STATUS_FILTERS: (RegistrationStatus | 'ALL')[] = ['ALL', 'NEW', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'ONBOARDED']

const statusTone: Record<string, string> = {
  NEW: 'bg-blue-100 text-blue-700',
  UNDER_REVIEW: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
  ONBOARDED: 'bg-teal-100 text-teal-700',
}

function StatusBadge({ status }: { status: RegistrationStatus }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusTone[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {SUPPORTER_STATUS_LABELS[status]}
    </span>
  )
}

const SupportersReview = React.memo(() => {
  const [regs, setRegs] = useState<SupporterRegistration[]>([])
  const [stats, setStats] = useState<Record<string, number>>({})
  const [statusFilter, setStatusFilter] = useState<RegistrationStatus | 'ALL'>('ALL')
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actingId, setActingId] = useState<number | null>(null)
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; msg: string } | null>(null)
  const [viewing, setViewing] = useState<SupporterRegistration | null>(null)
  const size = 15

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [data, s] = await Promise.all([
        getSupporterRegistrations({ status: statusFilter, page, size }),
        getSupporterStats(),
      ])
      setRegs(data.content)
      setTotal(data.totalElements)
      setTotalPages(data.totalPages)
      setStats(s)
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load registrations.')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, page])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(0) }, [statusFilter])

  const flash = (tone: 'success' | 'error', msg: string) => {
    setToast({ tone, msg })
    setTimeout(() => setToast(null), 3000)
  }

  const changeStatus = async (r: SupporterRegistration, status: RegistrationStatus) => {
    setActingId(r.id)
    try {
      await updateSupporterStatus(r.id, status)
      flash('success', `“${r.name}” → ${SUPPORTER_STATUS_LABELS[status]}`)
      await load()
    } catch (e: any) {
      flash('error', e?.response?.data?.message || 'Failed to update.')
    } finally {
      setActingId(null)
    }
  }

  const remove = async (r: SupporterRegistration) => {
    if (!window.confirm(`Delete the registration from “${r.name}”?`)) return
    setActingId(r.id)
    try {
      await deleteSupporterRegistration(r.id)
      flash('success', 'Registration deleted')
      await load()
    } catch (e: any) {
      flash('error', e?.response?.data?.message || 'Failed to delete.')
    } finally {
      setActingId(null)
    }
  }

  return (
    <GovLayout showSidebar={false}>
      <div className="space-y-6">
        <GovPageTitle title="Supporter Registrations" subtitle="Review TB Supporter (Ni-kshay Mitra), nutrition and volunteer registrations" icon={HandHeart} />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <GovCard className="text-center"><p className="text-2xl font-bold text-gray-900">{stats.TOTAL ?? 0}</p><p className="text-sm text-gray-500">Total</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-blue-600">{stats.NEW ?? 0}</p><p className="text-sm text-gray-500">New</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-amber-600">{stats.UNDER_REVIEW ?? 0}</p><p className="text-sm text-gray-500">Under review</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-teal-600">{stats.ONBOARDED ?? 0}</p><p className="text-sm text-gray-500">Onboarded</p></GovCard>
        </div>

        <GovCard>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-700 mr-1">Status:</span>
            {STATUS_FILTERS.map((f) => (
              <button key={f} onClick={() => setStatusFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${statusFilter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {f === 'ALL' ? 'All' : SUPPORTER_STATUS_LABELS[f]}
              </button>
            ))}
            <span className="ml-auto text-xs text-gray-400">{total} total</span>
          </div>
        </GovCard>

        {toast && (
          <div role="status" className={`flex items-center gap-2 p-3 rounded-lg text-sm ${toast.tone === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {toast.tone === 'success' ? '✓' : <AlertCircle className="w-4 h-4" />} {toast.msg}
          </div>
        )}

        <GovCard>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-gray-900">Registrations</h2>
            <button onClick={load} disabled={loading} className="text-xs text-blue-600 hover:underline disabled:opacity-50">{loading ? 'Loading…' : 'Refresh'}</button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
          ) : error ? (
            <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg text-sm"><AlertCircle className="w-4 h-4" /> {error}</div>
          ) : regs.length === 0 ? (
            <GovEmptyState icon="🤝" title="No registrations" description="No supporter registrations match this filter." />
          ) : (
            <div className="divide-y divide-gray-100">
              {regs.map((r) => {
                const isOrg = ['NIKSHAY_MITRA_NGO', 'NIKSHAY_MITRA_CORPORATE'].includes(r.registrationType)
                return (
                  <div key={r.id} className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <StatusBadge status={r.status} />
                          <span className="text-xs text-gray-400">{r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}</span>
                        </div>
                        <h4 className="font-medium text-gray-900 flex items-center gap-1.5">
                          {isOrg && <Building2 className="w-3.5 h-3.5 text-gray-400" />}
                          {r.name}
                          {r.organizationName && <span className="font-normal text-gray-500">· {r.organizationName}</span>}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {r.email}{r.phone && ` · ${r.phone}`}{r.city && ` · ${r.city}, ${r.state ?? ''}`}
                        </p>
                        <p className="text-[11px] text-gray-400 mt-1">
                          {REGISTRATION_TYPE_LABELS[r.registrationType]}
                          {r.preferredSupportType && ` · ${SUPPORT_TYPE_LABELS[r.preferredSupportType]}`}
                          {r.numberOfPatients ? ` · ${r.numberOfPatients} patients` : ''}
                        </p>
                        {r.message && <p className="text-sm text-gray-700 mt-1 line-clamp-2">{r.message}</p>}
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                        {actingId === r.id && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                        <button onClick={() => setViewing(r)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md" aria-label="View"><Eye className="w-4 h-4" /></button>
                        <select value={r.status} onChange={(e) => changeStatus(r, e.target.value as RegistrationStatus)} disabled={actingId === r.id}
                          className="text-xs border border-gray-200 rounded-md px-2 py-1 disabled:opacity-50" aria-label="Change status">
                          {(['NEW', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'ONBOARDED'] as RegistrationStatus[]).map((s) => <option key={s} value={s}>{SUPPORTER_STATUS_LABELS[s]}</option>)}
                        </select>
                        <button onClick={() => remove(r)} disabled={actingId === r.id} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>Previous</GovButton>
              <span className="text-xs text-gray-500">Page {page + 1} of {totalPages}</span>
              <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next</GovButton>
            </div>
          )}
        </GovCard>

        {viewing && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" onClick={() => setViewing(null)}>
            <div className="bg-white rounded-xl max-w-xl w-full max-h-[80vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <StatusBadge status={viewing.status} />
                  <h3 className="text-xl font-bold text-gray-900 mt-2">{viewing.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{viewing.email}{viewing.phone && ` · ${viewing.phone}`}</p>
                </div>
                <button onClick={() => setViewing(null)} className="text-gray-400 hover:text-gray-700" aria-label="Close">✕</button>
              </div>
              <div className="text-xs text-gray-500 space-y-0.5 border-t border-gray-100 pt-3">
                <p><b>Type:</b> {REGISTRATION_TYPE_LABELS[viewing.registrationType]}</p>
                {viewing.organizationName && <p><b>Organization:</b> {viewing.organizationName}</p>}
                {viewing.preferredSupportType && <p><b>Support:</b> {SUPPORT_TYPE_LABELS[viewing.preferredSupportType]}</p>}
                {viewing.supportTypeDetail && <p><b>Support detail:</b> {viewing.supportTypeDetail}</p>}
                {viewing.patientSupportOption && <p><b>Option:</b> {viewing.patientSupportOption}</p>}
                {viewing.city && <p><b>Location:</b> {viewing.city}{viewing.state ? `, ${viewing.state}` : ''}</p>}
                {(viewing.district || viewing.taluka) && <p><b>District / Taluka:</b> {viewing.district ?? '—'} / {viewing.taluka ?? '—'}</p>}
                {viewing.numberOfPatients ? <p><b>Patients:</b> {viewing.numberOfPatients}</p> : null}
                <p><b>Registered:</b> {viewing.createdAt ? new Date(viewing.createdAt).toLocaleString() : ''}</p>
              </div>
              {viewing.message && <p className="text-sm text-gray-700 whitespace-pre-wrap mt-3 border-t border-gray-100 pt-3">{viewing.message}</p>}
              {viewing.adminNotes && <p className="mt-4 text-xs text-orange-700 bg-orange-50 p-2 rounded">Admin notes: {viewing.adminNotes}</p>}
            </div>
          </div>
        )}
      </div>
    </GovLayout>
  )
})

SupportersReview.displayName = 'SupportersReview'
export default SupportersReview